import React, { useState } from 'react';
import { motion } from 'motion/react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useSEO } from '../hooks/useSEO';
import { Mail, MapPin, Clock } from 'lucide-react';

const ContactPage: React.FC = () => {
  useSEO({
    title: 'Contact Us — Aura Traveler',
    description: 'Get in touch with the Aura Traveler team. Enterprise inquiries, partnership opportunities, and concierge support.',
    canonical: 'https://www.auratraveler.com/contact',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'Contact', url: 'https://www.auratraveler.com/contact' },
    ],
  });

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      if (!db) throw new Error('Firestore unavailable');
      await addDoc(collection(db, 'contacts'), {
        name: formData.name,
        email: formData.email,
        topic: formData.subject,
        message: formData.message,
        createdAt: serverTimestamp(),
      });
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const inputClasses = "w-full px-6 py-4 bg-white/[0.03] border border-white/10 text-white font-sans text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/40 transition-colors duration-300";

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      <SiteNav />

      <main>
      {/* Hero */}
      <section className="pt-40 pb-16 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold/5 blur-[120px] rounded-full -mr-64 -mt-64 pointer-events-none" />
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-gold/60 mb-8 block">
              Get in Touch
            </span>
            <h1 className="font-display text-5xl sm:text-7xl md:text-8xl text-white tracking-tighter leading-[0.9] mb-6">
              Let's <span className="italic font-serif text-white/80">Connect</span>
            </h1>
            <p className="font-serif text-lg text-white/50 italic max-w-xl leading-relaxed">
              Whether you're exploring a partnership, have a question about our platform, or want to bring Aura to your agency — we'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 md:px-12 lg:px-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">

          {/* Contact Form */}
          <div className="lg:col-span-3">
            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="py-16 px-8 border border-gold/30 bg-gold/[0.05] text-center"
              >
                <h3 className="font-display text-2xl text-white mb-4">Message Received</h3>
                <p className="font-serif text-white/60 italic">We'll be in touch within 24 hours.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={inputClasses}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={inputClasses}
                  />
                </div>
                <select
                  name="topic"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  required
                  className={`${inputClasses} appearance-none`}
                >
                  <option value="" className="bg-charcoal">Select a Topic</option>
                  <option value="enterprise" className="bg-charcoal">Enterprise Inquiry</option>
                  <option value="agency" className="bg-charcoal">Agency Partnership</option>
                  <option value="hotel" className="bg-charcoal">Hotel Integration</option>
                  <option value="support" className="bg-charcoal">Customer Support</option>
                  <option value="press" className="bg-charcoal">Press & Media</option>
                  <option value="other" className="bg-charcoal">Other</option>
                </select>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className={`${inputClasses} resize-none`}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-12 py-5 bg-gold text-black font-sans text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-white transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
                >
                  {status === 'loading' ? 'Sending...' : 'Send Message'}
                </button>
                {status === 'error' && (
                  <p className="font-sans text-[11px] text-red-400/80 tracking-wide">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}
              </form>
            )}
          </div>

          {/* Contact Info */}
          <aside className="lg:col-span-2 space-y-12">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <Mail className="w-5 h-5 text-gold/60" />
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50">Email</span>
              </div>
              <a href="mailto:concierge@auratraveler.com" className="font-serif text-white/70 hover:text-gold transition-colors duration-300">
                concierge@auratraveler.com
              </a>
            </div>

            <div>
              <div className="flex items-center gap-4 mb-4">
                <MapPin className="w-5 h-5 text-gold/60" />
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50">Headquarters</span>
              </div>
              <p className="font-serif text-white/70">
                Los Angeles, California
              </p>
            </div>

            <div>
              <div className="flex items-center gap-4 mb-4">
                <Clock className="w-5 h-5 text-gold/60" />
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50">Response Time</span>
              </div>
              <p className="font-serif text-white/70">
                Within 24 hours
              </p>
            </div>

            <div className="pt-8 border-t border-white/5">
              <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/30 mb-4">Enterprise & Agency</p>
              <p className="font-serif text-sm text-white/50 leading-relaxed">
                For white-label, API access, or multi-seat agency deployments, email us directly or select "Enterprise Inquiry" above.
              </p>
            </div>
          </aside>

        </div>
      </section>

      </main>

      <SiteFooter />
    </div>
  );
};

export default ContactPage;
